import React, { useState, useEffect } from 'react';
import { Zap, X, ChevronRight, Calendar, ChevronsRight, ChevronDown } from 'lucide-react';
import { Select, ConfigProvider, DatePicker, theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import { useTheme } from '../../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const AddGoalModal = ({ isOpen, onClose, onSubmit, goalType, setGoalType, goal = null, minWeightage = 10, maxWeightage = 100, projectsList = [] }) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const isEditMode = !!goal;

  // Weightage options: multiples of 10 from minWeightage to maxWeightage
  const weightageOptions = React.useMemo(() => {
    const opts = [];
    for (let w = minWeightage; w <= maxWeightage; w += 10) {
      opts.push({ label: `${w}%`, value: w });
    }
    return opts;
  }, [minWeightage, maxWeightage]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isCritical: false,
    category: 'Performance Goal',
    target: '100',
    unit: '%',
    weightage: minWeightage,
    projectId: '',
  });

  useEffect(() => {
    if (goal) {
      // Parse goal data for edit mode
      const metricMatch = goal.metric?.match(/^=\s*(\d+)(%)?$/);
      const targetValue = metricMatch ? metricMatch[1] : (goal.metric?.replace(/[^0-9]/g, '') || '100');
      const unitValue = goal.metric?.includes('%') ? '%' : 'Points';
      
      const rawWeight = goal.weightage ?? goal.weight ?? minWeightage;
      const weightRounded = Math.round(Number(rawWeight) / 10) * 10;
      const weightInRange = Math.max(minWeightage, Math.min(maxWeightage, weightRounded));
      setFormData({
        title: goal.name || '',
        description: goal.description || '',
        isCritical: goal.critical || false,
        category: goal.category || 'Performance Goal',
        target: targetValue,
        unit: unitValue,
        weightage: weightInRange,
        projectId: goal.projectId?._id || goal.projectId || '',
        startDate: goal.startDate ? dayjs(goal.startDate) : null,
        endDate: goal.endDate ? dayjs(goal.endDate) : null,
      });
    } else {
      // Set default dates based on goal type
      const now = new Date();
      let start, end;
      if (goalType === 'monthly') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else {
        const nextMonday = new Date(now);
        nextMonday.setDate(now.getDate() + ((7 - now.getDay() + 1) % 7 || 7));
        const nextSunday = new Date(nextMonday);
        nextSunday.setDate(nextMonday.getDate() + 6);
        start = nextMonday;
        end = nextSunday;
      }
      
      // Find General Project as default
      const generalProject = projectsList.find(p => p.name?.toLowerCase().trim() === 'general project' || p.isGeneralProject);
      setFormData({
        title: '',
        description: '',
        isCritical: false,
        category: 'Performance Goal',
        target: '100',
        unit: '%',
        weightage: minWeightage,
        projectId: generalProject?._id || '',
        startDate: dayjs(start),
        endDate: dayjs(end),
      });
    }
  }, [goal, isOpen, goalType, minWeightage, projectsList]);

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      goalType,
      startDate: formData.startDate ? formData.startDate.format('DD - MM - YYYY') : '',
      endDate: formData.endDate ? formData.endDate.format('DD - MM - YYYY') : '',
    });
    setFormData({
      title: '',
      description: '',
      isCritical: false,
      category: 'Performance Goal',
      target: '100',
      unit: '%',
      weightage: minWeightage,
      projectId: '',
      startDate: null,
      endDate: null,
    });
    onClose();
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: 20,
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3,
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: {
        duration: 0.2,
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.1,
        duration: 0.3,
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[2px]"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
        <motion.div
          className={`w-full max-w-[750px] rounded-[2rem] shadow-2xl overflow-hidden relative flex flex-col text-left ${
            isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'
          }`}
          style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-10 pt-10 pb-6 flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border ${
                isDark
                  ? 'bg-blue-500/20 text-blue-400 border-blue-500/20'
                  : 'bg-blue-50 text-blue-600 border-blue-100'
              }`}>
                <Zap size={24} strokeWidth={3} fill="currentColor" />
              </div>
              <div className="flex flex-col text-left">
                <h2 className={`text-2xl font-black tracking-tight leading-none mb-1 ${
                  isDark ? '!text-white' : 'text-[#0f172a]'
                }`}>
                  {isEditMode ? 'Edit Goal' : 'Add New Goal'}
                </h2>
                <p className={`text-[10px] font-black uppercase tracking-widest leading-none ${
                  isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                  {isEditMode ? 'UPDATE YOUR PERFORMANCE METRICS' : 'DEFINE YOUR PERFORMANCE METRICS'}
                </p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                isDark
                  ? 'bg-white/5 border-white/10 text-white/60 hover:!text-white hover:bg-white/10'
                  : 'bg-slate-50 border-slate-100 text-slate-300 hover:text-slate-900'
              }`}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <X size={20} strokeWidth={3} />
            </motion.button>
          </div>

          {/* Form */}
          <motion.div
            className="px-10 pb-10 space-y-6 overflow-y-auto max-h-[70vh] text-left"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="space-y-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
            >
              <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                GOAL TITLE *
              </label>
              <motion.input
                type="text"
                placeholder="e.g. Q1 Revenue Targets"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all ${
                  isDark
                    ? 'bg-zinc-800/50 border-white/10 text-white placeholder-white/40'
                    : 'bg-[#f8fafc] border-slate-200 text-slate-800'
                }`}
                whileFocus={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              />
            </motion.div>

            <div className="space-y-2">
              <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                DESCRIPTION *
              </label>
              <textarea
                placeholder="What exactly are you looking to achieve?"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all resize-none ${
                  isDark
                    ? 'bg-zinc-800/50 border-white/10 text-white placeholder-white/40'
                    : 'bg-[#f8fafc] border-slate-200 text-slate-800'
                }`}
              />
            </div>

            {/* Critical Goal Toggle */}
            <div className={`flex items-center gap-3 p-4 rounded-2xl border ${
              isDark
                ? 'bg-white/5 border-white/10'
                : 'bg-slate-50/50 border-slate-100'
            }`}>
              <label htmlFor="modal-is-critical" className="relative inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  id="modal-is-critical"
                  checked={formData.isCritical}
                  onChange={(e) => {
                    setFormData({ ...formData, isCritical: e.target.checked });
                  }}
                />
                <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${
                  formData.isCritical
                    ? 'bg-red-500'
                    : isDark
                      ? 'bg-white/10'
                      : 'bg-slate-200'
                }`}>
                  <div className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full border transition-transform duration-200 ${
                    formData.isCritical
                      ? 'translate-x-5 border-white'
                      : isDark
                        ? 'border-white/20'
                        : 'border-gray-300'
                  }`}></div>
                </div>
              </label>
              <div className="flex flex-col text-left">
                <label htmlFor="modal-is-critical" className={`text-[10px] font-black uppercase tracking-widest cursor-pointer ${
                  isDark ? 'text-white' : 'text-slate-600'
                }`}>
                  MARK AS CRITICAL GOAL
                </label>
                <span className={`text-[8px] font-bold uppercase tracking-tight ${
                  isDark ? 'text-white/60' : 'text-slate-300'
                }`}>
                  CRITICAL GOALS ARE PRIORITIZED IN REPORTS
                </span>
              </div>
            </div>

            {/* Project */}
            <div className="space-y-2 flex flex-col">
              <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                PROJECT
              </label>
              <ConfigProvider
                theme={{
                  algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                  token: {
                    borderRadius: 16,
                    controlHeight: 48,
                    colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                    colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                    colorText: isDark ? '#ffffff' : '#1e293b',
                    colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                    fontSize: 14,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  },
                  components: {
                    Select: {
                      optionSelectedBg: isDark ? 'rgba(37, 99, 235, 0.2)' : '#e0f2fe',
                      optionActiveBg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0',
                      optionColor: isDark ? 'white' : '#1e293b',
                    },
                  },
                }}
              >
                <Select
                  value={formData.projectId || undefined}
                  onChange={(value) => setFormData({ ...formData, projectId: value })}
                  className="w-full"
                  style={{ width: '100%' }}
                  placeholder="Select Project"
                  options={projectsList.map(p => ({ label: p.name, value: p._id }))}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  allowClear
                />
              </ConfigProvider>
            </div>

            {/* Category, Target, Unit */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2 flex flex-col">
                <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                  isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                  CATEGORY *
                </label>
                <ConfigProvider
                  theme={{
                    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                    token: {
                      borderRadius: 16,
                      controlHeight: 48,
                      colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                      colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                      colorText: isDark ? '#ffffff' : '#1e293b',
                      colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                      fontSize: 14,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    },
                    components: {
                      Select: {
                        optionSelectedBg: isDark ? 'rgba(37, 99, 235, 0.2)' : '#e0f2fe',
                        optionActiveBg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0',
                        optionColor: isDark ? 'white' : '#1e293b',
                      },
                    },
                  }}
                >
                  <Select
                    value={formData.category}
                    onChange={(value) => setFormData({ ...formData, category: value })}
                    className="w-full"
                    style={{
                      width: '100%',
                    }}
                    options={[
                      { label: 'Performance Goal', value: 'Performance Goal' },
                      { label: 'Learning Goal', value: 'Learning Goal' },
                    ]}
                  />
                </ConfigProvider>
              </div>
              <div className="space-y-2 flex flex-col">
                <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                  isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                  TARGET *
                </label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className={`w-full border rounded-2xl px-5 py-2 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all ${
                    isDark
                      ? 'bg-black border-white/5 text-white placeholder-white/30 focus:border-blue-500'
                      : 'bg-[#f8fafc] border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'
                  }`}
                />
              </div>
              <div className="space-y-2 flex flex-col">
                <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                  isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                  UNIT *
                </label>
                <ConfigProvider
                  theme={{
                    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                    token: {
                      borderRadius: 16,
                      controlHeight: 48,
                      colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                      colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                      colorText: isDark ? '#ffffff' : '#1e293b',
                      colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                      fontSize: 14,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    },
                    components: {
                      Select: {
                        optionSelectedBg: isDark ? 'rgba(37, 99, 235, 0.2)' : '#e0f2fe',
                        optionActiveBg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0',
                        optionColor: isDark ? 'white' : '#1e293b',
                      },
                    },
                  }}
                >
                  <Select
                    value={formData.unit}
                    onChange={(value) => setFormData({ ...formData, unit: value })}
                    className="w-full"
                    style={{
                      width: '100%',
                    }}
                    options={[
                      { label: '%', value: '%' },
                      { label: 'Points', value: 'Points' },
                    ]}
                  />
                </ConfigProvider>
              </div>
            </div>

            {/* Weightage (multiple of 10; non-critical goals must sum to 100%) */}
            <div className="space-y-2 flex flex-col">
              <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                WEIGHTAGE % *
              </label>
              <ConfigProvider
                theme={{
                  algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                  token: {
                    borderRadius: 16,
                    controlHeight: 48,
                    colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                    colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                    colorText: isDark ? '#ffffff' : '#1e293b',
                    colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                    fontSize: 14,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  },
                  components: {
                    Select: {
                      optionSelectedBg: isDark ? 'rgba(37, 99, 235, 0.2)' : '#e0f2fe',
                      optionActiveBg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0',
                      optionColor: isDark ? 'white' : '#1e293b',
                    },
                  },
                }}
              >
                <Select
                  value={formData.weightage}
                  onChange={(value) => setFormData({ ...formData, weightage: value })}
                  className="w-full"
                  style={{ width: '100%' }}
                  options={weightageOptions}
                />
              </ConfigProvider>
              <span className={`text-[8px] font-bold uppercase tracking-tight ${isDark ? 'text-white/50' : 'text-slate-400'}`}>
                Multiple of 10. Sum of non-critical goals must equal 100%.
              </span>
            </div>

            {/* Goal Type */}
            <div className="space-y-2 text-left">
              <label className={`text-[11px] font-black uppercase tracking-[0.05em] ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                GOAL TYPE *
              </label>
              <div className="flex gap-4">
                {['Monthly'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setGoalType(type.toLowerCase())}
                    className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                      goalType === type.toLowerCase()
                        ? 'bg-blue-600 !text-white border-blue-600 shadow-lg shadow-blue-500/20'
                        : isDark
                          ? 'bg-zinc-800/50 text-white/60 border-white/10 hover:border-blue-400'
                          : 'bg-[#f8fafc] text-slate-400 border-slate-200 hover:border-blue-400'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Period */}
            <div className="space-y-2 text-left">
              <label className={`text-[11px] font-black uppercase tracking-widest ${
                isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
                TIME PERIOD *
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full">
                <div className="flex-1 min-w-0 w-full sm:w-auto">
                  <ConfigProvider
                    theme={{
                      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                      token: {
                        borderRadius: 16,
                        controlHeight: 48,
                        colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                        colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                        colorText: isDark ? '#ffffff' : '#1e293b',
                        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                        fontSize: 14,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      },
                    }}
                  >
                    <DatePicker
                      value={formData.startDate}
                      onChange={(date) => setFormData({ ...formData, startDate: date })}
                      format="DD - MM - YYYY"
                      className="w-full"
                      placeholder="Start Date"
                      style={{ width: '100%', minWidth: '140px' }}
                      inputReadOnly={false}
                      allowClear={false}
                    />
                  </ConfigProvider>
                </div>
                <span className={`text-[10px] font-black uppercase whitespace-nowrap text-center sm:text-left ${
                  isDark ? 'text-white/60' : 'text-slate-300'
                }`}>
                  TO
                </span>
                <div className="flex-1 min-w-0 w-full sm:w-auto">
                  <ConfigProvider
                    theme={{
                      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                      token: {
                        borderRadius: 16,
                        controlHeight: 48,
                        colorBgContainer: isDark ? '#0a0a0a' : '#f8fafc',
                        colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                        colorText: isDark ? '#ffffff' : '#1e293b',
                        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
                        fontSize: 14,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      },
                    }}
                  >
                    <DatePicker
                      value={formData.endDate}
                      onChange={(date) => setFormData({ ...formData, endDate: date })}
                      format="DD - MM - YYYY"
                      className="w-full"
                      placeholder="End Date"
                      style={{ width: '100%', minWidth: '140px' }}
                      disabledDate={(current) => {
                        return formData.startDate && current && current < formData.startDate.startOf('day');
                      }}
                      inputReadOnly={false}
                      allowClear={false}
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            className={`px-10 py-8 border-t flex items-center gap-4 ${
              isDark
                ? 'bg-zinc-900/50 border-white/10'
                : 'bg-[#f8fafc]/30 border-slate-100'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <motion.button
              onClick={onClose}
              className={`flex-1 py-4 text-[12px] font-black uppercase tracking-[0.1em] border rounded-2xl transition-all ${
                isDark
                  ? 'text-white/60 border-white/10 hover:bg-white/10 hover:border-white/20'
                  : 'text-slate-400 border-slate-200 hover:bg-white hover:border-slate-300'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              CANCEL
            </motion.button>
            <motion.button
              onClick={handleSubmit}
              className="flex-[1.5] py-4 bg-blue-600 !text-white text-[12px] font-black uppercase tracking-[0.1em] rounded-2xl shadow-lg hover:bg-blue-700 hover:shadow-blue-500/30 flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(37, 99, 235, 0.4)" }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              {isEditMode ? 'UPDATE GOAL' : 'CREATE GOAL'}
              <ChevronsRight size={18} strokeWidth={3} />
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddGoalModal;
